import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'always'
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * r: THE STATEMENT REVEAL (sticky="scroll-up") — one number does two jobs
   * (measured against a reference header, 2026-09-22): the
   * depth above which the header is always at rest, and the distance the page must
   * travel in ONE direction before the bar changes state. Travel under it in either
   * direction changes nothing, which is what keeps small back-and-forth scrolls
   * from flapping the bar. The CSS half is THE STATEMENT REVEAL in
   * assets/r-header.css.
   */
  static REVEAL_DISTANCE = 100;

  /**
   * r: THE PEEK — how long the page must sit still, hidden header and all, before
   * the navigation bar alone slides back (2026-09-23, on direction). Long enough
   * that a pause inside a momentum scroll does not pop the bar, short enough to
   * read as "I stopped, the nav came back". No scroll event for this long is the
   * stop; `scrollend` would say the same thing but not in every browser we ship to.
   */
  static PEEK_DELAY = 300;

  /**
   * r: how far the page must move down before a PEEKING bar leaves again
   * (2026-09-23, phones). A peek used to go on the first pixel down, and on a
   * touchscreen the first pixels of a drag are noise: an up-drag that opened with
   * 2px of down movement hid the bar, and then owed a 100px run to bring it back.
   * A short run is still "the page moved on"; a mouse wheel clears it in one notch.
   */
  static PEEK_LEAVE_DISTANCE = 12;

  /** @type {number} */
  #revealPrevY = 0;

  /** @type {'up' | 'down'} */
  #revealPrevDirection = 'down';

  /**
   * Where the current run of scrolling in one direction began.
   * @type {number}
   */
  #revealAnchorY = 0;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #peekTimer = null;

  /**
   * A finger is on the page. Its scroll events stop whenever it rests, and that is
   * not the page stopping: the peek waits for it to lift (2026-09-23, phones).
   * Without this the bar popped back under a resting thumb and left again on the
   * next movement — the flapping that made the phone feel unlike the desktop.
   */
  #touching = false;

  /**
   * The scroll range's far end, measured off the main path: reading scrollHeight
   * inside the scroll handler can force a layout on every frame, and on a phone
   * mid-fling that is the frame. Kept by an observer on the body instead.
   * @type {number}
   */
  #maxScrollY = Infinity;

  /** @type {ResizeObserver | null} */
  #rangeObserver = null;

  /**
   * r: THE PACE (2026-09-23, on direction). A state written while the bar, the
   * mark or the plate is still moving retargets that transition — CSS then plays
   * the new one over less than its declared time (the reversing-shortening rule),
   * and the peek timer did it to nearly every short flick: the bar was ~60% out
   * when the page stopped, and turned straight around. Every reveal and hide now
   * runs to its end. A state requested meanwhile is parked here, and applied when
   * the last running transition ends — only the latest request, since the page
   * has moved on. A parked peek restarts its clock from the settle instead, so a
   * bar that left comes back only after the page has been still for PEEK_DELAY.
   * @type {'rest' | 'hidden' | 'peek' | 'revealed' | null}
   */
  #pendingBarState = null;

  /**
   * The reveal's transitions in flight, one entry per element × property that
   * `transitionrun` announced and `transitionend` / `transitioncancel` has not yet
   * retired. Only the moves: the bar's transform, the mark's and plate's translate.
   * @type {Set<string>}
   */
  #barMoves = new Set();

  /**
   * Nothing the reveal runs takes this long; a missed end event must not park a
   * state for good.
   */
  static PACE_TIMEOUT = 1500;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #paceTimer = null;

  /**
   * r: a mega menu opened while the bar was peeking has faded the mark in at the
   * panel's foot (see the peek rule in assets/r-header.css). When that panel
   * closes the mark stays out, on its plate — the header goes to `revealed`
   * rather than tucking the mark away again (2026-09-23, on direction). The
   * flag remembers that the panel was opened from the peek; a panel opened from
   * `revealed` or `rest` changes nothing on close.
   */
  #menuOpenedFromPeek = false;

  /**
   * header-menu.js writes aria-expanded on the <header-menu> itself as a panel
   * opens and closes; the links' own aria-expanded flips per item and is ignored.
   * @type {MutationObserver | null}
   */
  #menuObserver = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * (sticky="always" only — scroll-up runs THE STATEMENT REVEAL instead)
   */
  #observeStickyPosition = () => {
    if (this.#intersectionObserver) return;

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        this.dataset.stickyState = entry.isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      },
      { threshold: 1 }
    );

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');

    if (stickyMode === 'scroll-up') {
      this.#updateRevealState(document.scrollingElement?.scrollTop ?? 0);
      return;
    }

    if (stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (isAtTop) {
      this.dataset.scrollDirection = 'none';
    } else if (isScrollingUp) {
      this.dataset.scrollDirection = 'up';
    } else {
      this.dataset.scrollDirection = 'down';
    }

    this.#lastScrollTop = scrollTop;
  };

  /**
   * r: rest / hidden / peek / revealed, written to `data-r-bar`. Upstream's scroll-up mode
   * (fade the bar out, fade it back) never ran here: the header group is sticky for
   * any [sticky], so its IntersectionObserver never saw the header leave. This
   * replaces it outright.
   *
   * The direction logic: a run of scrolling one way is measured
   * from where that run began, and only a run longer than REVEAL_DISTANCE commits.
   * Above REVEAL_DISTANCE from the top the header is at rest whatever the
   * direction, which is what hands the page back its hero ink at the top.
   *
   * @param {number} scrollTop
   */
  #updateRevealState(scrollTop) {
    // A dialog or drawer owns the page while scroll-lock is set; the scroll
    // position cannot move under it, and the bar must not change behind it.
    if (document.documentElement.hasAttribute('scroll-lock')) return;

    const distance = HeaderComponent.REVEAL_DISTANCE;
    // Clamped to the scroll range: a rubber-band past either end (iOS) reads as
    // travel and then as a run back, which revealed the bar at the foot of a page.
    const y = Math.min(Math.max(0, scrollTop), this.#maxScrollY);
    const direction = y >= this.#revealPrevY ? 'down' : 'up';
    const anchor = direction === this.#revealPrevDirection ? this.#revealAnchorY : this.#revealPrevY;

    const state = this.dataset.rBar;
    // A peeking bar leaves on a short run down: it came back because the page
    // stopped, so the page moving on is the cue to go. Everything else waits for
    // a full run.
    const leaving =
      state === 'peek' ? y > anchor + HeaderComponent.PEEK_LEAVE_DISTANCE : y > anchor + distance;

    if (y < distance) {
      this.#setBarState('rest');
    } else if (direction === 'down' && leaving) {
      // Held on screen (an open panel, keyboard focus), the bar stays — as the
      // peek, unless it is already the full reveal. Never left at `rest` mid-page:
      // that pins the mark in its hero ink over whatever scrolls under it.
      if (!this.#holdsBar()) this.#setBarState('hidden');
      else if (state !== 'revealed') this.#setBarState('peek');
    } else if (direction === 'up' && y < anchor - distance) {
      this.#setBarState('revealed');
    }

    this.#revealPrevY = y;
    this.#revealPrevDirection = direction;
    this.#revealAnchorY = anchor;
    this.#armPeek();
  }

  /**
   * Every scroll restarts the clock; when it runs out with the header hidden, the
   * navigation bar comes back on its own — the PEEK. The wordmark stays tucked: it
   * returns only with a deliberate scroll up (the reveal above), so stopping to read
   * brings back the menu without a 140px band over the page.
   */
  #armPeek() {
    if (this.#peekTimer !== null) clearTimeout(this.#peekTimer);
    this.#peekTimer = setTimeout(() => {
      this.#peekTimer = null;
      // Every early return below has an owner that re-arms this — THE STOP IS
      // NEVER DROPPED (2026-09-29): a resting finger re-arms it when it lifts
      // (#handleTouchEnd); a move in flight or a parked state re-arms it from the
      // settle whenever the bar ends up hidden (#settleBarMoves); a scroll lock
      // re-arms it when the lock lifts (#handleScrollLock).
      if (this.#touching) return;
      if (this.#pendingBarState !== null || this.#barMoves.size > 0) return;
      if (this.dataset.rBar !== 'hidden') return;
      if (document.documentElement.hasAttribute('scroll-lock')) return;
      // The peek's own run starts here, so PEEK_LEAVE_DISTANCE is measured from
      // where the page stopped, not from where the last run down began.
      this.#revealAnchorY = this.#revealPrevY;
      this.#setBarState('peek');
    }, HeaderComponent.PEEK_DELAY);
  }

  #measureScrollRange = () => {
    const scroller = document.scrollingElement;
    this.#maxScrollY = scroller ? Math.max(0, scroller.scrollHeight - scroller.clientHeight) : Infinity;
  };

  /** @param {TouchEvent} event */
  #handleTouchStart = (event) => {
    this.#touching = true;
    // Touch events stay with the node the finger went down on, and if that node
    // leaves the document mid-touch (a carousel re-render, a morph) the lift
    // never bubbles to `document` — #touching would stay true and hold the peek
    // off for good. Listening on the node itself hears it either way.
    const target = event.target;
    if (target instanceof EventTarget && target !== document) {
      target.addEventListener('touchend', this.#handleTouchEnd, { once: true, passive: true });
      target.addEventListener('touchcancel', this.#handleTouchEnd, { once: true, passive: true });
    }
  };

  /** @param {TouchEvent} event */
  #handleTouchEnd = (event) => {
    if (event.touches.length > 0) return;
    if (!this.#touching) return;
    this.#touching = false;
    // The finger is up; if the page is not moving either, the peek clock runs from
    // now. A momentum scroll keeps re-arming it until it stops, as before.
    if (this.dataset.rBar === 'hidden') this.#armPeek();
  };

  /**
   * An open mega-menu panel or keyboard focus inside the header keeps the bar on
   * screen: hiding it would carry the panel, or the focused control, away with it.
   * `:focus-visible` rather than `:focus-within`, so a mouse click on a header
   * button (which leaves focus there) does not pin the bar for the rest of the page.
   */
  #holdsBar() {
    return Boolean(this.querySelector('.menu-list__link[aria-expanded="true"], :focus-visible'));
  }

  /**
   * Keyboard focus arriving in a hidden header brings it back (Shift+Tab from the
   * page, a skip link) rather than leaving it off screen.
   * @param {FocusEvent} event
   */
  #handleRevealFocus = (event) => {
    if (this.dataset.rBar !== 'hidden') return;
    if (!(event.target instanceof Element) || !event.target.matches(':focus-visible')) return;
    // The bar is what focus landed in; the wordmark is not needed for that.
    this.#setBarState('peek');
  };

  /**
   * @param {'rest' | 'hidden' | 'peek' | 'revealed'} state
   */
  #setBarState(state) {
    if (this.#barMoves.size > 0) {
      // The latest request wins, and a request for the state already on screen
      // is no request: parking it (every scroll event of a flick past the
      // threshold re-asks for `hidden` while the bar is leaving) made the settle
      // a no-op that never re-armed the peek, and the bar stayed away on a page
      // that had stopped (2026-09-29).
      this.#pendingBarState = state === this.dataset.rBar ? null : state;
      return;
    }
    this.#pendingBarState = null;
    if (this.dataset.rBar === state) return;
    // Where it came from, written first so one style change sees both: on a phone
    // the same target state moves differently depending on it — see PHONES in
    // assets/r-header.css.
    this.dataset.rBarFrom = this.dataset.rBar ?? '';
    this.dataset.rBar = state;
  }

  /**
   * Which of the reveal's moving parts a transition event is about, or null for
   * everything else (the ink, visibility, the mega-menu ride).
   * @param {TransitionEvent} event
   */
  #barMoveKey(event) {
    const { target, propertyName } = event;
    if (!(target instanceof Element)) return null;
    // transform / translate on desktop; the position longhand on phones (see OFF
    // THE COMPOSITOR in assets/r-header.css), reported physical or logical.
    const moves =
      propertyName === 'transform' ||
      propertyName === 'translate' ||
      propertyName === 'top' ||
      propertyName === 'inset-block-start';
    if (!moves) return null;
    // Per element × property: one element can run two moves at once (the mark's
    // position and the mega-menu ride), and the first to end must not retire both.
    if (target.classList.contains('header-section')) return `bar:${propertyName}`;
    if (target.classList.contains('r-header-statement-logo')) return `mark:${propertyName}`;
    if (target.classList.contains('r-header-statement-plate')) return `plate:${propertyName}`;
    return null;
  }

  /** @param {TransitionEvent} event */
  #handleBarMoveStart = (event) => {
    const key = this.#barMoveKey(event);
    if (!key) return;
    this.#barMoves.add(key);
    if (this.#paceTimer !== null) clearTimeout(this.#paceTimer);
    this.#paceTimer = setTimeout(this.#settleBarMoves, HeaderComponent.PACE_TIMEOUT);
  };

  /** @param {TransitionEvent} event */
  #handleBarMoveEnd = (event) => {
    const key = this.#barMoveKey(event);
    if (!key) return;
    this.#barMoves.delete(key);
    if (this.#barMoves.size === 0) this.#settleBarMoves();
  };

  /**
   * A drawer or dialog closing hands the page back without a scroll event; a bar
   * that was hidden under it owes the peek from here.
   */
  #handleScrollLock = () => {
    if (document.documentElement.hasAttribute('scroll-lock')) return;
    if (this.dataset.rBar === 'hidden') this.#armPeek();
  };

  /** @type {MutationObserver | null} */
  #scrollLockObserver = null;

  /** @param {MutationRecord[]} records */
  #handleMenuExpanded = (records) => {
    for (const record of records) {
      const target = record.target;
      if (!(target instanceof Element) || target.localName !== 'header-menu') continue;
      if (target.getAttribute('aria-expanded') === 'true') {
        this.#menuOpenedFromPeek = this.dataset.rBar === 'peek';
      } else if (this.#menuOpenedFromPeek) {
        this.#menuOpenedFromPeek = false;
        // Same frame as the attribute: the peek rule would otherwise start the
        // tuck before this lands. `revealed` keeps the mark where it is and
        // slides the plate in behind it.
        if (this.dataset.rBar === 'peek') this.#setBarState('revealed');
      }
    }
  };

  #settleBarMoves = () => {
    this.#barMoves.clear();
    if (this.#paceTimer !== null) clearTimeout(this.#paceTimer);
    this.#paceTimer = null;
    const pending = this.#pendingBarState;
    this.#pendingBarState = null;
    // A parked peek restarts its clock from here rather than jumping in.
    if (pending !== null && pending !== 'peek') this.#setBarState(pending);
    // A bar that has just left on a page that stopped meanwhile still owes a
    // peek — whatever was or was not parked. If the page is still moving, its
    // next scroll event re-arms the clock anyway.
    if (this.dataset.rBar === 'hidden') this.#armPeek();
  };

  /**
   * First state, written without motion. Until it lands the mark runs the CSS
   * scroll track (the no-JS fallback), so on a page restored mid-scroll the mark is
   * already tucked; transitioning from there would first drop it back to rest and
   * then slide it away. `data-r-bar-init` switches the reveal transitions off for
   * the two frames it takes the new state to be styled.
   */
  #initRevealState() {
    const y = document.scrollingElement?.scrollTop ?? 0;
    this.#revealPrevY = y;
    this.#revealAnchorY = y;
    this.dataset.rBarInit = '';
    this.#setBarState(y < HeaderComponent.REVEAL_DISTANCE ? 'rest' : 'hidden');
    requestAnimationFrame(() => requestAnimationFrame(() => delete this.dataset.rBarInit));
    // A page restored mid-scroll is a page that has stopped: it peeks too.
    this.#armPeek();
  }

  /**
   * r: THE LOGO ON THE PAGE IT LINKS TO (2026-09-28, on direction). Every brand
   * mark in the header links home; on the home page itself a click scrolls the
   * page back to the top, smoothly, instead of reloading it. Keyed on the link
   * resolving to the current path rather than on a template flag, so a market or
   * language prefix (`/en-ca`) and a query string (a preview theme) still count.
   * Modified and non-primary clicks keep the browser's behaviour (new tab, new
   * window), and reduced motion jumps. The header's own scroll handling then
   * reveals the bar on the way up and settles it at rest at the top.
   * @param {MouseEvent} event
   */
  #handleLogoClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;
    if (!link.matches('.r-header-statement-logo__link, .r-header-bar-wordmark, .header-logo')) return;
    if (link.target && link.target !== '_self') return;
    const url = new URL(link.href, location.href);
    const path = (/** @type {string} */ value) => value.replace(/\/+$/, '') || '/';
    if (url.origin !== location.origin || path(url.pathname) !== path(location.pathname)) return;
    event.preventDefault();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduce ? 'instant' : 'smooth' });
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.addEventListener('click', this.#handleLogoClick);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode === 'always') {
      this.#observeStickyPosition();
      document.addEventListener('scroll', this.#handleWindowScroll);
    } else if (stickyMode === 'scroll-up') {
      this.#initRevealState();
      document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      document.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
      document.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
      document.addEventListener('touchcancel', this.#handleTouchEnd, { passive: true });
      this.addEventListener('focusin', this.#handleRevealFocus);
      this.#rangeObserver = new ResizeObserver(this.#measureScrollRange);
      this.#rangeObserver.observe(document.body);
      window.addEventListener('resize', this.#measureScrollRange, { passive: true });
      // The bar's transform runs on the header section, this element's parent;
      // the group hears it and everything inside.
      const group = this.closest('#header-group') ?? this.parentElement ?? this;
      group.addEventListener('transitionrun', this.#handleBarMoveStart);
      group.addEventListener('transitionend', this.#handleBarMoveEnd);
      group.addEventListener('transitioncancel', this.#handleBarMoveEnd);
      this.#menuObserver = new MutationObserver(this.#handleMenuExpanded);
      this.#menuObserver.observe(this, { attributes: true, subtree: true, attributeFilter: ['aria-expanded'] });
      this.#scrollLockObserver = new MutationObserver(this.#handleScrollLock);
      this.#scrollLockObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['scroll-lock'] });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    if (this.#peekTimer !== null) clearTimeout(this.#peekTimer);
    this.#peekTimer = null;
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.removeEventListener('focusin', this.#handleRevealFocus);
    this.removeEventListener('click', this.#handleLogoClick);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.removeEventListener('touchstart', this.#handleTouchStart);
    document.removeEventListener('touchend', this.#handleTouchEnd);
    document.removeEventListener('touchcancel', this.#handleTouchEnd);
    window.removeEventListener('resize', this.#measureScrollRange);
    this.#rangeObserver?.disconnect();
    const group = this.closest('#header-group') ?? this.parentElement ?? this;
    group.removeEventListener('transitionrun', this.#handleBarMoveStart);
    group.removeEventListener('transitionend', this.#handleBarMoveEnd);
    group.removeEventListener('transitioncancel', this.#handleBarMoveEnd);
    if (this.#paceTimer !== null) clearTimeout(this.#paceTimer);
    this.#paceTimer = null;
    this.#barMoves.clear();
    this.#pendingBarState = null;
    this.#menuObserver?.disconnect();
    this.#menuObserver = null;
    this.#scrollLockObserver?.disconnect();
    this.#scrollLockObserver = null;
    this.#touching = false;
    this.#menuOpenedFromPeek = false;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      // The header itself is excluded: it is in normal flow and already counted by
      // the group. (It used to be added back when it was transparent and therefore
      // absolutely positioned — that mode is gone, 2026-09.)
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (entry.target !== header) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
